<template>
  <div ref="chartContainer" class="w-[80%] mx-auto">
    <!-- Header with Title and Download Button -->
    <div class="mb-2 flex items-center justify-center relative">
      <div class="text-center">
        <h4 class="text-sm font-bold text-gray-900">{{ metricLabel }} by High- versus Low- Quality</h4>
      </div>
      <div class="absolute right-0 top-0">
        <ChartDownloadButton
          :chart-instance="chartInstance"
          chart-name="curve-feature-comparison-chart"
          :is-chart-ready="isChartReady"
          :container-element="chartContainer"
        />
      </div>
    </div>
    <CurveResultsBaseChart
      ref="baseChart"
      :chart-type="'scatter'"
      :chart-data="chartData"
      :chart-options="chartOptions"
      :chart-plugins="chartPlugins"
      :width="700"
      :height="240"
      :auto-update="true"
      @chart-ready="onChartReady"
    />
    <CurveSelectionTooltip ref="curveSelectionTooltip" from-source="curve-results" />
  </div>
</template>

<script>
import CurveResultsBaseChart from './CurveResultsBaseChart.vue'
import CurveSelectionTooltip from './CurveSelectionTooltip.vue'
import ChartDownloadButton from './ChartDownloadButton.vue'

export default {
  name: 'CurveFeatureComparisonChart',
  components: { 
    CurveResultsBaseChart,
    CurveSelectionTooltip,
    ChartDownloadButton
  },
  props: {
    validValues: { type: Array, required: true },
    invalidValues: { type: Array, required: true },
    metricLabel: { type: String, required: true },
    units: { type: String, default: '' },
    validCurveIndices: { type: Array, default: () => [] },
    invalidCurveIndices: { type: Array, default: () => [] }
  },
  data() {
    return {
      chartData: null,
      chartOptions: null,
      chartPlugins: [],
      chartInstance: null,
      isChartReady: false,
      chartContainer: null
    }
  },
  watch: {
    validValues: { immediate: true, handler() { this.buildChart() } },
    invalidValues: { immediate: true, handler() { this.buildChart() } },
    metricLabel() { this.buildChart() }
  },
  methods: {
    computeStats(values) {
      const arr = (values || []).map(v => Number(v)).filter(v => isFinite(v))
      if (!arr.length) return null
      const sorted = [...arr].sort((a, b) => a - b)
      const q = (p) => {
        if (!sorted.length) return null
        const pos = (sorted.length - 1) * p
        const base = Math.floor(pos)
        const rest = pos - base
        if (sorted[base + 1] !== undefined) {
          return sorted[base] + rest * (sorted[base + 1] - sorted[base])
        }
        return sorted[base]
      }
      const min = sorted[0]
      const max = sorted[sorted.length - 1]
      const q1 = q(0.25)
      const median = q(0.5)
      const q3 = q(0.75)
      return { min, q1, median, q3, max }
    },
    buildChart() {
      const valid = (this.validValues || []).map(v => Number(v)).filter(v => isFinite(v))
      const invalid = (this.invalidValues || []).map(v => Number(v)).filter(v => isFinite(v))
      const validStats = this.computeStats(valid)
      const invalidStats = this.computeStats(invalid)

      const xLabel = this.metricLabel + (this.units ? ` (${this.units})` : '')

      // Position groups near the vertical center using a linear y scale
      const validY = 0.6
      const invalidY = 0.4
      const scatterValid = valid.map((v, index) => ({ 
        x: v, 
        y: validY,
        curveIndex: this.validCurveIndices[index] || index
      }))
      const scatterInvalid = invalid.map((v, index) => ({ 
        x: v, 
        y: invalidY,
        curveIndex: this.invalidCurveIndices[index] || index
      }))

      // Build plugins: jitter (vertical), and horizontal box/whisker overlay
      const jitterPlugin = {
        id: 'jitterScatterY',
        afterDatasetsUpdate: (chart) => {
          const jitterPx = 3
          chart.data.datasets.forEach((ds, di) => {
            if (ds.type !== 'scatter') return
            const meta = chart.getDatasetMeta(di)
            meta.data.forEach((elem, i) => {
              const originalY = elem.y
              const seed = (i + 1) * (di + 7)
              const rand = Math.sin(seed) * 10000
              const frac = rand - Math.floor(rand)
              const offset = (frac - 0.5) * 2 * jitterPx
              elem.y = originalY + offset
            })
          })
        }
      }

      const boxWhiskerPlugin = {
        id: 'boxWhiskerOverlayHorizontal',
        afterDatasetsDraw: (chart) => {
          const ctx = chart.ctx
          const xScale = chart.scales['x']
          const yScale = chart.scales['y']
          if (!xScale || !yScale) return

          const drawOne = (yValue, stats, color) => {
            if (!stats) return
            const y = yScale.getPixelForValue(yValue)
            const halfHeight = 12

            const xMin = xScale.getPixelForValue(stats.min)
            const xQ1 = xScale.getPixelForValue(stats.q1)
            const xMed = xScale.getPixelForValue(stats.median)
            const xQ3 = xScale.getPixelForValue(stats.q3)
            const xMax = xScale.getPixelForValue(stats.max)

            ctx.save()
            ctx.lineWidth = 2
            ctx.strokeStyle = color
            ctx.fillStyle = color.replace('1)', '0.12)')

            // Whiskers (horizontal)
            ctx.beginPath()
            ctx.moveTo(xMin, y)
            ctx.lineTo(xQ1, y)
            ctx.moveTo(xQ3, y)
            ctx.lineTo(xMax, y)
            ctx.stroke()

            // Whisker caps (vertical small)
            ctx.beginPath()
            ctx.moveTo(xMin, y - halfHeight * 0.6)
            ctx.lineTo(xMin, y + halfHeight * 0.6)
            ctx.moveTo(xMax, y - halfHeight * 0.6)
            ctx.lineTo(xMax, y + halfHeight * 0.6)
            ctx.stroke()

            // Box (horizontal)
            ctx.beginPath()
            ctx.rect(xQ1, y - halfHeight, xQ3 - xQ1, halfHeight * 2)
            ctx.fill()
            ctx.stroke()

            // Median line (vertical)
            ctx.beginPath()
            ctx.moveTo(xMed, y - halfHeight)
            ctx.lineTo(xMed, y + halfHeight)
            ctx.stroke()

            ctx.restore()
          }

          drawOne(validY, validStats, 'rgba(34,197,94,1)')
          drawOne(invalidY, invalidStats, 'rgba(239,68,68,1)')
        }
      }

      // Tooltip plugin removed - now using shared ChartTooltip component

      this.chartData = {
        datasets: [
          {
            type: 'scatter',
            label: 'High-Quality',
            data: scatterValid,
            backgroundColor: 'rgba(34,197,94,0.35)',
            borderColor: 'rgba(34,197,94,0.9)',
            pointRadius: 3,
            pointHoverRadius: 4
          },
          {
            type: 'scatter',
            label: 'Low-Quality',
            data: scatterInvalid,
            backgroundColor: 'rgba(239,68,68,0.35)',
            borderColor: 'rgba(239,68,68,0.9)',
            pointRadius: 3,
            pointHoverRadius: 4
          }
        ]
      }

      const globalMin = Math.min(
        ...valid.map(v => Number(v)).filter(v => isFinite(v)),
        ...invalid.map(v => Number(v)).filter(v => isFinite(v))
      )
      const globalMax = Math.max(
        ...valid.map(v => Number(v)).filter(v => isFinite(v)),
        ...invalid.map(v => Number(v)).filter(v => isFinite(v))
      )
      const pad = (globalMax - globalMin) * 0.05 || 1

      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        parsing: false,
        scales: {
          x: {
            type: 'linear',
            title: { display: true, text: xLabel },
            suggestedMin: isFinite(globalMin) ? globalMin - pad : undefined,
            suggestedMax: isFinite(globalMax) ? globalMax + pad : undefined,
            grid: { display: true },
            ticks: { maxTicksLimit: 6 }
          },
          y: {
            type: 'linear',
            title: { display: true, text: 'Group' },
            min: 0.35,
            max: 0.65,
            grid: { display: false },
            ticks: {
              maxTicksLimit: 2,
              callback: (value) => {
                if (Math.abs(value - validY) < 1e-6) return 'High-Quality'
                if (Math.abs(value - invalidY) < 1e-6) return 'Low-Quality'
                return ''
              }
            }
          }
        },
        plugins: {
          legend: { display: true },
          tooltip: {
            enabled: false, // Disable default tooltip since we're using external
            external: (context) => {
              // Use shared tooltip component
              const { chart, tooltip } = context
              const tooltipComponent = this.$refs.curveSelectionTooltip
              
              if (!tooltipComponent) return
              
              // Show tooltip only when hovering over data points
              if (tooltip && tooltip.dataPoints && tooltip.dataPoints.length > 0) {
                const point = tooltip.dataPoints[0].raw
                if (point) {
                  // Find all curves with the same value (like FlagThresholdChart does)
                  const sameValueCurves = []
                  const pointValue = Number(point.x).toFixed(6)
                  
                  // Check valid curves
                  this.validValues.forEach((value, index) => {
                    if (Number(value).toFixed(6) === pointValue) {
                      const curveIndex = this.validCurveIndices[index] || index
                      sameValueCurves.push(curveIndex)
                    }
                  })
                  
                  // Check invalid curves
                  this.invalidValues.forEach((value, index) => {
                    if (Number(value).toFixed(6) === pointValue) {
                      const curveIndex = this.invalidCurveIndices[index] || index
                      sameValueCurves.push(curveIndex)
                    }
                  })
                  
                  // Create tooltip content using shared component
                  const content = tooltipComponent.createTooltipHtml(point.x, sameValueCurves)
                  tooltipComponent.updateTooltipContent(content)
                  
                  // Position tooltip
                  const canvasRect = chart.canvas.getBoundingClientRect()
                  const mouseX = canvasRect.left + tooltip.caretX
                  const mouseY = canvasRect.top + tooltip.caretY
                  
                  tooltipComponent.positionTooltip(mouseX, mouseY)
                }
              } else {
                // Hide tooltip when not hovering over data points
                tooltipComponent.hideTooltip()
              }
            }
          }
        },
        onHover: (event, elements) => {
          // Handle tooltip show/hide using shared component
          const tooltipComponent = this.$refs.curveSelectionTooltip
          if (elements.length === 0 && tooltipComponent) {
            tooltipComponent.hideTooltip()
          }
        }
      }

      this.chartPlugins = [jitterPlugin, boxWhiskerPlugin]
      console.log('📊 Chart plugins registered:', this.chartPlugins.map(p => p.id))
    },
    onChartReady(chart) {
      this.chartInstance = chart
      this.isChartReady = chart && !chart.destroyed
    }
  },
  mounted() {
    // Set container reference after mount
    this.$nextTick(() => {
      this.chartContainer = this.$refs.chartContainer
    })
  },
  beforeUnmount() {
    // Reset chart state on unmount
    this.chartInstance = null
    this.isChartReady = false
    this.chartContainer = null
  }
}
</script>

<style scoped>
</style>


